import classNames from 'classnames';
import React, { Fragment, useId, useMemo, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import omit from 'lodash/omit';

import { ID } from '../../../../../types';
import { FetchLifestyleDetection } from '../../../../lifestyleDetections/queries/lifestyleDetectionsByImageUrl.query';
import {
  FetchProductsCacheKey,
  FetchProductsFilters
} from '../../../../products/productsTypes';
import { ThreeDStockProductsListProduct } from '../../lists/ThreeDStockProductsList';

import { useCacheGroupedByDetectedIdProducts } from '../../../../products/hooks/useCacheGroupedByDetectedIdProducts';

import {
  ThreeDStockProductsList,
  ThreeDStockProductsListProps
} from '../../lists/ThreeDStockProductsList';
import { ThreeDStockDetectedObjectMatches } from '../ThreeDStockDetectedObjectMatches';

import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { LoadingGridSkeleton } from '../../../../../helpers/LoadingGridSkeleton';
import { NoResults } from '../../../../../helpers/NoResults';
import { Translate } from '../../../../../helpers/Translate';

import { aiSearchKeys } from '../../../../../locales/keys';

interface ThreeDStockAllObjectsProductsContentProps
  extends ThreeDStockProductsListProps {
  detections: FetchLifestyleDetection[];
  activeDetectionId?: ID | null;
  onActiveDetectionChange?: (id: ID | null) => void;
  initialFilters: FetchProductsFilters;
  lifestyleId?: ID;
  cacheKey: FetchProductsCacheKey;
}

function ThreeDStockAllObjectsProductsContent({
  detections,
  initialFilters,
  lifestyleId,
  cacheKey,
  activeDetectionId,
  onActiveDetectionChange,
  ...listProps
}: ThreeDStockAllObjectsProductsContentProps) {
  const panelId = useId();
  const [expandedId, setExpandedId] = useState<ID | null>(null);
  const { ref, width = 0 } = useResizeObserver<HTMLDivElement>();
  const columns = Math.max(
    1,
    Math.floor((width + 20) / (listProps.cellMinSize + 20))
  );
  const filters = useMemo(
    () => ({
      ...omit(initialFilters, [
        'similarImage',
        'similarProduct',
        'similarProductImageId',
        'similarProductsByCustomArea',
        'similarProductsByObjectInLifestyle',
        'searchByDetectedId',
        'searchByDetectedIds'
      ]),
      searchByDetectedIds: {
        detectionIds: detections.map((detection) => String(detection.id)),
        lifestyleId
      }
    }),
    [initialFilters, detections, lifestyleId]
  );
  const groupedCacheKey = `${cacheKey}-all-objects-best-matches-${JSON.stringify(
    [filters, detections.map((detection) => detection.detectRequestId)]
  )}`;

  const bestMatchCellSize = width
    ? Math.max(1, Math.floor((width - (columns - 1) * 20) / columns))
    : listProps.cellMinSize;
  const {
    groupedByDetectedIdProducts,
    groupedByDetectedIdProductsError,
    groupedByDetectedIdProductsFetched
  } = useCacheGroupedByDetectedIdProducts<ThreeDStockProductsListProduct>({
    cacheKey: groupedCacheKey,
    initialFilters: filters,
    initialLimit: 1,
    options: {
      enabled: detections.length > 0,
      enabledPlaceholder: false,
      keepPreviousData: false
    }
  });

  const productsByDetection = useMemo(
    () =>
      new Map(
        groupedByDetectedIdProducts.map((group) => [
          String(group.detectedId),
          group.products.slice(0, 1)
        ])
      ),
    [groupedByDetectedIdProducts]
  );

  const expandedIndex = detections.findIndex(
    (detection) => detection.id === expandedId
  );
  const expandedRowEnd =
    expandedIndex < 0
      ? -1
      : Math.min(
          detections.length - 1,
          Math.floor(expandedIndex / columns) * columns + columns - 1
        );
  const expandedColumn = expandedIndex % columns;
  const expandedPointerLeft = width
    ? ((expandedColumn + 0.5) * (width + 20)) / columns - 10
    : '50%';
  const loaded = detections.length === 0 || groupedByDetectedIdProductsFetched;

  return (
    <div ref={ref} className="w-full min-w-0">
      <AlertMessage message={groupedByDetectedIdProductsError} />
      <LoadingGridSkeleton loaded={loaded}>
        {detections.length === 0 && <NoResults />}
        <div
          className="grid items-start gap-5"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {detections.map((detection, index) => {
            const products =
              productsByDetection.get(String(detection.id)) || [];
            const expanded = detection.id === expandedId;

            return (
              <Fragment key={detection.id}>
                <section
                  onMouseEnter={() => onActiveDetectionChange?.(detection.id)}
                  onMouseLeave={() => onActiveDetectionChange?.(null)}
                  onFocus={() => onActiveDetectionChange?.(detection.id)}
                  onBlur={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                      onActiveDetectionChange?.(null);
                    }
                  }}
                  className={classNames(
                    'min-w-0 flex flex-col gap-3 rounded-lg',
                    activeDetectionId === detection.id &&
                      'ring-2 ring-blue-600 ring-offset-4 dark:ring-offset-gray-900'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="flex min-w-0 items-center gap-2 font-semibold text-gray-900 dark:text-white">
                      <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md bg-gray-100 text-sm dark:bg-gray-700">
                        {index + 1}
                      </span>
                      <span className="truncate capitalize">
                        {detection.category}
                      </span>
                    </h3>
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={expanded ? panelId : undefined}
                      onClick={() =>
                        setExpandedId(expanded ? null : detection.id)
                      }
                      className="group/match ml-auto shrink-0 text-xs font-normal text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:underline focus-visible:underline"
                    >
                      <span className="group-hover/match:hidden group-focus-visible/match:hidden">
                        <Translate id={aiSearchKeys.bestMatch} />
                      </span>
                      <span className="hidden group-hover/match:inline group-focus-visible/match:inline">
                        <Translate
                          id={
                            expanded
                              ? aiSearchKeys.hideMoreMatches
                              : aiSearchKeys.showMoreMatches
                          }
                        />
                      </span>
                    </button>
                  </div>
                  <ThreeDStockProductsList
                    {...listProps}
                    cellMinSize={bestMatchCellSize}
                    products={products}
                    productsTotalCount={products.length}
                    productsError={null}
                    productsFetched={true}
                    productsIsPlaceholderData={false}
                    productsFetchingNextPage={false}
                    hasNextProductsPage={false}
                    productsPage={1}
                    productsLimit={1}
                    productsFilters={filters}
                  />
                </section>
                {index === expandedRowEnd && (
                  <div
                    id={panelId}
                    className="relative col-span-full min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900"
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute -top-2 h-4 w-4 -translate-x-1/2 rotate-45 border-l border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
                      style={{ left: expandedPointerLeft }}
                    />
                    <div className="mb-4 flex items-center justify-between gap-4">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        <Translate id={aiSearchKeys.moreMatches} />
                        {' · '}
                        {expandedIndex + 1}.{' '}
                        {detections[expandedIndex].category}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setExpandedId(null)}
                        className="text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                      >
                        <Translate id={aiSearchKeys.closeMatches} />
                      </button>
                    </div>
                    <ThreeDStockDetectedObjectMatches
                      key={String(expandedId)}
                      detection={detections[expandedIndex]}
                      initialFilters={initialFilters}
                      lifestyleId={lifestyleId}
                      cacheKey={cacheKey}
                      listProps={listProps}
                    />
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      </LoadingGridSkeleton>
    </div>
  );
}

export default ThreeDStockAllObjectsProductsContent;
